import { useState, useMemo, useCallback, useEffect } from "react";
import { Plus, Edit2, Trash, Calendar } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Select } from "antd";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
} from "@/services/APIs";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm, onFieldReact, onFieldValueChange } from "@formily/core";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import LoadingSpinner from "@/components/common/LoadingSpinner";

const LEAVE_APPLICATION_SCHEMA_NAME = "PTS Leave Application";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

function isEmployeeSchemaField(
  key: string,
  field: Record<string, any> | null | undefined
): boolean {
  const keyLower = key.toLowerCase();
  const fieldTitle = String(
    field?.title ??
      field?.["x-decorator-props"]?.title ??
      field?.["x-component-props"]?.title ??
      ""
  ).toLowerCase();
  const componentFieldName = field?.["x-component-props"]?.field_name
    ? String(field["x-component-props"].field_name).toLowerCase()
    : "";

  return (
    keyLower === "emp" ||
    keyLower === "employee" ||
    keyLower === "employee_id" ||
    keyLower.includes("employee") ||
    componentFieldName === "emp" ||
    componentFieldName.includes("employee") ||
    fieldTitle.includes("employee")
  );
}

function getSchemaProperties(schema: any): Record<string, any> | null {
  if (schema?.schema?.properties) {
    return schema.schema.properties;
  }
  if (schema?.properties) {
    return schema.properties;
  }
  return null;
}

function findEmployeePropertyKey(schema: any): string | null {
  const properties = getSchemaProperties(schema);
  if (!properties) {
    return null;
  }

  for (const key of Object.keys(properties)) {
    if (isEmployeeSchemaField(key, properties[key])) {
      return key;
    }
  }
  return null;
}

function isAllEmployeesValue(value: unknown): boolean {
  return value === true || value === "true" || value === 1 || value === "1";
}

const LeaveApplicationPage = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingLeaveApplicationId, setEditingLeaveApplicationId] = useState<
    string | null
  >(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Create formily form
  const form = useMemo(() => createForm({}), []);

  // Fetch schema information with pagination
  const { data: schemaInfo, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema", LEAVE_APPLICATION_SCHEMA_NAME],
    queryFn: () =>
      SchemaService.getSchemaByName(LEAVE_APPLICATION_SCHEMA_NAME, 1, 50),
    enabled: !!LEAVE_APPLICATION_SCHEMA_NAME,
  });

  // Fetch employee dropdown for employee name mapping
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getItemsByPage(
        EMPLOYEE_SCHEMA_NAME,
        1,
        10000, // Large page size to get all employees
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Transform employee options to show first name + last name
  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }

    return employeeOptions.map((employee: { value: string; label: string }) => {
      // Find the employee record by ID
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );

      if (employeeRecord) {
        // Get first name and last name from the record
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";

        // Combine first name and last name
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          value: employee.value,
          label: fullName || employee.label, // Fallback to original label if names not found
        };
      }

      // Fallback to original label if employee record not found
      return employee;
    });
  }, [
    employeeOptions,
    employeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  // Get column grid data to generate all columns dynamically
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", LEAVE_APPLICATION_SCHEMA_NAME],
    queryFn: () =>
      SchemaService.getColumnGridData(LEAVE_APPLICATION_SCHEMA_NAME),
  });

  // Find employee field name from column grid data for filtering (API may use "emp" or "employee")
  const employeeFieldName = useMemo(() => {
    if (!columnGridData?.grid) {
      return null;
    }
    for (const field of columnGridData.grid) {
      const fieldName = field.field_name.toLowerCase();
      const fieldTitle = field.field_title?.toLowerCase() ?? "";
      if (
        fieldName === "emp" ||
        fieldName.includes("employee") ||
        fieldTitle.includes("employee")
      ) {
        return field.field_name;
      }
    }
    return null;
  }, [columnGridData]);

  const formEmployeeFieldKey = useMemo(() => {
    const schema = schemaInfo?.json ?? schemaInfo;
    return findEmployeePropertyKey(schema) ?? employeeFieldName ?? "emp";
  }, [schemaInfo, employeeFieldName]);

  // When "Is All Employee" is on, Employee is optional and cleared so create can submit.
  useEffect(() => {
    const effectId = "leave-application-is-all-emp";
    form.addEffects(effectId, () => {
      const syncEmployeeRequired = (isAll: boolean) => {
        form.setFieldState(formEmployeeFieldKey, state => {
          state.required = !isAll;
          state.disabled = isAll;
          if (isAll) {
            state.value = null;
            state.selfErrors = [];
          }
        });
      };

      onFieldValueChange("is_all_emp", field => {
        syncEmployeeRequired(isAllEmployeesValue(field.value));
      });

      onFieldReact(formEmployeeFieldKey, field => {
        const isAll = isAllEmployeesValue(form.values?.is_all_emp);
        field.required = !isAll;
        field.disabled = isAll;
        if (isAll) {
          field.setSelfErrors([]);
        }
      });
    });

    return () => {
      form.removeEffects(effectId);
    };
  }, [form, formEmployeeFieldKey]);

  // Fetch leave application data for editing
  const { data: leaveApplicationData, isLoading: leaveApplicationLoading } =
    useQuery({
      queryKey: [
        "leave-application-data",
        LEAVE_APPLICATION_SCHEMA_NAME,
        editingLeaveApplicationId,
      ],
      queryFn: () => {
        if (!editingLeaveApplicationId) {
          throw new Error("Leave Application ID is required");
        }
        return DynamicModuleService.getItemById(
          LEAVE_APPLICATION_SCHEMA_NAME,
          editingLeaveApplicationId
        );
      },
      enabled: !!editingLeaveApplicationId,
    });

  // Set form values when in edit mode - use setValues to update existing form
  useEffect(() => {
    if (isEditMode && leaveApplicationData) {
      const cleanedData = removeMetaFields(leaveApplicationData);
      const empKey = formEmployeeFieldKey;
      const isAllEmployees = isAllEmployeesValue(cleanedData.is_all_emp);

      cleanedData.is_all_emp = isAllEmployees;
      if (isAllEmployees) {
        cleanedData[empKey] = null;
        cleanedData.emp = null;
      }

      form.setValues(cleanedData);
      form.setFieldState(empKey, state => {
        state.required = !isAllEmployees;
        state.disabled = isAllEmployees;
      });
    } else if (!isEditMode) {
      // Reset form when not in edit mode
      form.reset();
    }
  }, [isEditMode, leaveApplicationData, form, formEmployeeFieldKey]);

  // Helper function to format date from ISO string to DD-MM-YYYY
  const formatDate = useCallback(
    (dateString: string | null | undefined): string => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return "-";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "-";
      }
    },
    []
  );

  // Helper function to get employee name by ID (first name + last name)
  const getEmployeeNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return null;
      }

      const idString = String(id);

      // First try to find in employee records to get first_name and last_name
      const employeeRecord = employeeRecords.find((record: any) => {
        const recordId =
          record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";
        return String(recordId) === idString;
      });

      if (employeeRecord) {
        // Get first name and last name from the record
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          employeeRecord.firstName ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          employeeRecord.lastName ??
          "";

        // Combine first name and last name
        const fullName = `${firstName} ${lastName}`.trim();

        if (fullName) {
          return fullName;
        }
      }

      // Fallback to employeeOptionsWithNames (which already has names)
      const employeeWithName = employeeOptionsWithNames?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === idString
      );

      if (employeeWithName?.label) {
        return employeeWithName.label;
      }

      // Final fallback to dropdown options (support both value/id and label/name)
      const employeeOption = employeeOptions?.find(
        (item: {
          value?: string;
          label?: string;
          id?: string;
          name?: string;
        }) => String(item.value ?? (item as { id?: string }).id) === idString
      );

      return (
        (employeeOption as { label?: string; name?: string })?.label ??
        (employeeOption as { label?: string; name?: string })?.name ??
        null
      );
    },
    [
      employeeOptions,
      employeeRecords,
      employeeFirstNameField,
      employeeLastNameField,
      employeeOptionsWithNames,
    ]
  );

  // Generate all columns dynamically and customize Employee field
  const columns: ProColumns<any>[] = useMemo(() => {
    const handleDelete = async (id: string | undefined) => {
      if (!id) {
        notify.error("Leave Application ID is missing");
        return;
      }
      try {
        const response = await DynamicModuleService.deleteItem(
          LEAVE_APPLICATION_SCHEMA_NAME,
          String(id)
        );
        if (response) {
          queryClient.invalidateQueries({
            queryKey: ["moduleData", LEAVE_APPLICATION_SCHEMA_NAME],
          });
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete leave application");
      }
    };

    const handleEdit = (id: string | undefined) => {
      if (!id) {
        notify.error("Leave Application ID is missing");
        return;
      }
      setEditingLeaveApplicationId(String(id));
      setIsEditMode(true);
      // Scroll to form
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (!columnGridData?.grid) {
      return [];
    }

    const dataColumns = columnGridData.grid.map((field: any) => {
      const fieldName = field.field_name.toLowerCase();
      const originalFieldName = field.field_name;
      const fieldTitle = field.field_title;

      // Check if this is an Employee field (API may use "emp", "employee", "employee_id", or title "Employee")
      const isEmployeeField =
        (fieldName === "emp" || fieldName.includes("employee")) ??
        fieldTitle?.toLowerCase().includes("employee") ??
        false;

      // Check if this is a date field (use ?? so any match applies formatting)
      const isDateField =
        fieldName.includes("date") ??
        fieldName.includes("from_date") ??
        fieldName.includes("to_date") ??
        fieldName.includes("start_date") ??
        fieldName.includes("end_date") ??
        fieldTitle?.toLowerCase().includes("date") ??
        false;

      const baseColumn: ProColumns<any> = {
        title: fieldTitle,
        dataIndex: fieldName,
        key: fieldName,
      };

      // Render Employee field with name from dropdown/records
      if (isEmployeeField) {
        baseColumn.render = (_: any, entity: any) => {
          if (isAllEmployeesValue(entity?.is_all_emp)) {
            return (
              <span className="font-medium text-gray-900">All Employees</span>
            );
          }

          // Try multiple key shapes (API may return emp, employee_id, employee, Employee, etc.)
          const value =
            entity?.[fieldName] ??
            entity?.[originalFieldName] ??
            entity?.[field.field_name] ??
            entity?.emp ??
            entity?.employee_id ??
            entity?.employeeId ??
            entity?.Employee;
          if ((value ?? "") === "") {
            return "-";
          }
          const employeeName = getEmployeeNameById(value);
          return (
            <span className="font-medium text-gray-900">
              {employeeName ?? value}
            </span>
          );
        };
      }

      // Format date fields to DD-MM-YYYY
      if (isDateField) {
        baseColumn.render = (_: any, entity: any) => {
          // Try both lowercase and original field name to handle different API responses
          const value =
            entity?.[fieldName] ??
            entity?.[originalFieldName] ??
            entity?.[field.field_name];
          return formatDate(value);
        };
      }

      return baseColumn;
    });

    return [
      ...dataColumns,
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: any) => {
          const recordId =
            record?.id ?? record?.ID ?? record?.Id ?? record?._id;

          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => {
                handleEdit(recordId);
              }}
              title="Edit Leave Application"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this leave application?"
              onConfirm={() => {
                handleDelete(recordId);
              }}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton
                danger
                icon={<Trash size={16} />}
                type="text"
                title="Delete Leave Application"
              />
            </Popconfirm>,
          ];
        },
      },
    ];
  }, [columnGridData, queryClient, formatDate, getEmployeeNameById]);

  // Handle form submission (both create and update)
  const handleSubmitLeaveApplication = async () => {
    try {
      const empKey = formEmployeeFieldKey;
      const isAllEmployees = isAllEmployeesValue(form.values?.is_all_emp);

      // Is All Employee ON → Employee not required; clear value before validate
      if (isAllEmployees) {
        form.setFieldState(empKey, state => {
          state.required = false;
          state.disabled = true;
          state.value = null;
          state.selfErrors = [];
        });
      }

      await form.validate();
      setIsSubmitting(true);

      const modifiedValues = removeMetaFields(form.values);
      const payload: Record<string, unknown> = {
        ...modifiedValues,
        is_all_emp: isAllEmployees,
      };

      if (isAllEmployees) {
        payload[empKey] = null;
        payload.emp = null;
      } else {
        const employeeId =
          modifiedValues[empKey] ??
          modifiedValues.emp ??
          modifiedValues.employee ??
          null;
        payload[empKey] = employeeId;
        payload.emp = employeeId;
      }

      if (isEditMode && editingLeaveApplicationId) {
        if (!checkFormChanges(payload, leaveApplicationData, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          LEAVE_APPLICATION_SCHEMA_NAME,
          editingLeaveApplicationId,
          payload
        );

        if (response) {
          handleCancelEdit();
          queryClient.invalidateQueries({
            queryKey: ["moduleData", LEAVE_APPLICATION_SCHEMA_NAME],
          });
        }
      } else {
        const response = await DynamicModuleService.createItem(
          LEAVE_APPLICATION_SCHEMA_NAME,
          payload
        );

        if (response) {
          form.reset();
          queryClient.invalidateQueries({
            queryKey: ["moduleData", LEAVE_APPLICATION_SCHEMA_NAME],
          });
        }
      }
    } catch {
      // Form validation errors are shown on fields; API errors handled by service
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingLeaveApplicationId(null);
    form.reset();
  };

  // Build filter object for employee filter
  const employeeFilter = useMemo(() => {
    const filter: Record<string, any> = {
      ordering: "-created_on", // Order by most recent first
    };

    // Add employee filter if a specific employee is selected (not "all")
    if (selectedEmployee && selectedEmployee !== "all" && employeeFieldName) {
      filter[`${employeeFieldName}__eq`] = selectedEmployee;
    }

    return filter;
  }, [selectedEmployee, employeeFieldName]);

  const isLoading = schemaLoading || (isEditMode && leaveApplicationLoading);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Leave Applications
          </h1>
          <p className="text-sm text-[#627384]">
            Manage employee leave applications and records
          </p>
        </div>
      </div>

      {/* Create/Edit Leave Application Section */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-[#111827]">
            {isEditMode ? (
              <>
                <Edit2 className="h-5 w-5" />
                Edit Leave Application
              </>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                Create New Leave Application
              </>
            )}
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <SchemaFormView
                  key={`form-${isEditMode ? "edit" : "create"}-${editingLeaveApplicationId ?? "new"}`}
                  form={form}
                  schemaJson={schemaInfo?.json ?? schemaInfo}
                />
              </div>

              <div className="flex justify-end gap-3">
                {isEditMode && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handleSubmitLeaveApplication}
                  disabled={isSubmitting || isLoading}
                  className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
                >
                  {isEditMode ? (
                    <>
                      <Edit2 className="h-4 w-4" />
                      {isSubmitting
                        ? "Updating..."
                        : "Update Leave Application"}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {isSubmitting
                        ? "Creating..."
                        : "Create Leave Application"}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Leave Application Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-[#111827]">
                Leave Application List
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Employee:
              </label>
              <Select
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                placeholder="Select employee"
                style={{ width: 200 }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[
                  { value: "all", label: "All Employees" },
                  ...(employeeOptionsWithNames ?? []),
                ]}
              />
            </div>
          </div>
          <ConfigDynamicTable
            tableName={LEAVE_APPLICATION_SCHEMA_NAME}
            columns={columns}
            headerTitle=""
            rowKey="id"
            filter={employeeFilter}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveApplicationPage;
